// Nudge system types + content-versioning map.
//
// A "nudge" is any piece of in-app guidance (onboarding tour step, contextual
// banner, empty-state hint, success toast). Each nudge has a stable `id`.
//
// Two lifecycle concepts:
//  - "dismissed": the user (or the system) closed it and it should not return.
//  - "seen":      it was shown / completed (e.g. a finished tour); used to avoid
//                 auto-triggering again without implying the user rejected it.

export type NudgeId = string;

export const NUDGE_STORAGE_KEY = "dismissedNudges";

// persisted shape (stored under NUDGE_STORAGE_KEY in localStorage)
export interface NudgeState {
  // id -> the content version it was dismissed at
  dismissed: Record<NudgeId, number>;
  seen: NudgeId[];
}

/**
 * Current content version per nudge id. Bump a value when the nudge's copy or
 * behaviour changes meaningfully; `isDismissed` then returns false once, so the
 * nudge is shown again to users who previously dismissed it.
 */
export const NUDGE_VERSIONS: Record<NudgeId, number> = {
  "primary-tour": 1,
  "setup-first-assistant": 1,
  "finish-assistant": 1,
  "empty-assistants": 1,
  "empty-campaigns": 1,
  "empty-contacts": 1,
  "empty-call-logs": 1,
  "empty-knowledge-base": 1,
};

export interface NudgeContext {
  /** True when the nudge was dismissed at a version >= its current version. */
  isDismissed: (id: NudgeId) => boolean;
  /** Mark a nudge dismissed at its current version. */
  dismiss: (id: NudgeId) => void;
  /** True when the nudge has been shown/completed at least once. */
  isSeen: (id: NudgeId) => boolean;
  /** Mark a nudge as shown/completed (does not imply dismissal). */
  markSeen: (id: NudgeId) => void;
  /** Clear all dismissed + seen state (dev / "show tours again"). */
  resetAll: () => void;
}

export type Nullable<T> = T | null;
